import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";

export enum ActivityLevel {
  LOW = "LOW", // 0–45 min
  MEDIUM = "MEDIUM", // 45–90 min
  HIGH = "HIGH", // 90+ min
}

export const ActivityLevelMeta = {
  [ActivityLevel.LOW]: {
    label: "0–45 minutes/day",
    min: 0,
    max: 45,
  },
  [ActivityLevel.MEDIUM]: {
    label: "45–90 minutes/day",
    min: 45,
    max: 90,
  },
  [ActivityLevel.HIGH]: {
    label: "90+ minutes/day",
    min: 90,
    max: Infinity,
  },
};

export enum TreatFrequency {
  HEAVY_SNACKER = "HEAVY_SNACKER",
  OCCASIONAL = "OCCASIONAL",
  NEVER = "NEVER",
}

export const TreatFrequencyMeta = {
  [TreatFrequency.HEAVY_SNACKER]: { label: "Heavy Snacker" },
  [TreatFrequency.OCCASIONAL]: { label: "Occasional" },
  [TreatFrequency.NEVER]: { label: "Never" },
};

// -----------------------------------------

export enum Allergy {
  BEEF = "Beef",
  CHICKEN = "Chicken",
  TURKEY = "Turkey",
  LAMB = "Lamb",
  FISH = "Fish",
}

export const AllergyMeta = Object.fromEntries(
  Object.values(Allergy).map((v) => [v, { label: v }])
);

// -----------------------------------------

export enum HealthIssue {
  SENSITIVE_STOMACH = "Sensitive stomach",
  SKIN_ISSUES = "Skin issues",
  JOINT_MOBILITY = "Joint/mobility issues",
  WEIGHT_ISSUES = "Weight issues",
  KIDNEY_ISSUES = "Kidney issues",
  LIVER_ISSUES = "Liver issues",
  HEART_ISSUES = "Heart issues",
  DIABETES = "Diabetes",
  THYROID_ISSUES = "Thyroid issues",
  URINARY_PROBLEMS = "Urinary problems",
  DENTAL_PROBLEMS = "Dental problems",
  ALLERGIES_ENV = "Allergies (environmental)",
  ANXIETY = "Anxiety / stress",
  SEIZURES = "Seizures",
  OTHER = "Other",
}

export const HealthIssueMeta = Object.fromEntries(
  Object.values(HealthIssue).map((v) => [v, { label: v }])
);

/** Sentinel the health-issues question stores for "No health issues" — filter
 *  it out of anything that leaves the quiz (storage, Klaviyo, reassurance). */
export const HEALTH_ISSUE_NONE = "NONE_OPTION";

/** Same sentinel, stored by the allergies question for "No allergies". The
 *  step requires an answer, so "none" has to be a value — but it is a UI
 *  marker, not an allergy, and must not reach the customer record. */
export const ALLERGY_NONE = "NONE_OPTION";

/* ---- Issue #52 question cluster (current food, tried fresh, fussiness, goals) ---- */

export enum CurrentFood {
  DRY = "DRY",
  WET = "WET",
  RAW = "RAW",
  HOME_COOKED = "HOME_COOKED",
  FRESH = "FRESH",
}

export const CURRENT_FOOD_LABEL: Record<CurrentFood, string> = {
  [CurrentFood.DRY]: "Dry / kibble",
  [CurrentFood.WET]: "Wet / tinned",
  [CurrentFood.RAW]: "Raw",
  [CurrentFood.HOME_COOKED]: "Home-cooked",
  [CurrentFood.FRESH]: "Fresh food (like Paws)",
};

export enum TriedFresh {
  YES = "YES",
  NO = "NO",
}

export const TRIED_FRESH_LABEL: Record<TriedFresh, string> = {
  [TriedFresh.YES]: "Yes",
  [TriedFresh.NO]: "No",
};

export enum Fussiness {
  FOODIE = "FOODIE",
  GOOD_EATER = "GOOD_EATER",
  CAN_BE_FUSSY = "CAN_BE_FUSSY",
  VERY_FUSSY = "VERY_FUSSY",
}

export const FUSSINESS_LABEL: Record<Fussiness, string> = {
  [Fussiness.FOODIE]: "A foodie",
  [Fussiness.GOOD_EATER]: "A good eater",
  [Fussiness.CAN_BE_FUSSY]: "Can be fussy",
  [Fussiness.VERY_FUSSY]: "Very fussy",
};

export const isFussy = (value: unknown): boolean =>
  value === Fussiness.CAN_BE_FUSSY || value === Fussiness.VERY_FUSSY;

export enum FeedingGoal {
  SKIN_COAT = "SKIN_COAT",
  DIGESTION = "DIGESTION",
  WEIGHT = "WEIGHT",
  ENERGY = "ENERGY",
  FUSSY_MEALTIMES = "FUSSY_MEALTIMES",
  LONGEVITY = "LONGEVITY",
  OVERALL = "OVERALL",
}

export const FEEDING_GOAL_LABEL: Record<FeedingGoal, string> = {
  [FeedingGoal.SKIN_COAT]: "Healthier skin & coat",
  [FeedingGoal.DIGESTION]: "Better digestion / firmer poos",
  [FeedingGoal.WEIGHT]: "Weight management",
  [FeedingGoal.ENERGY]: "More energy & vitality",
  [FeedingGoal.FUSSY_MEALTIMES]: "Happier mealtimes (fussy eater)",
  [FeedingGoal.LONGEVITY]: "Long-term health & longevity",
  [FeedingGoal.OVERALL]: "Just overall health & happiness",
};

export enum BodyCondition {
  UNDERWEIGHT = "UNDERWEIGHT",
  IDEAL = "IDEAL",
  OVERWEIGHT = "OVERWEIGHT",
}

export enum Reproduction {
  NEUTERED = "NEUTERED",
  SPAYED = "SPAYED",
  NOT_NEUTERED = "NOT_NEUTERED",
  NOT_SPAYED = "NOT_SPAYED",
}

export enum Sex {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export const SEX_LABEL: Record<Sex, string> = {
  [Sex.MALE]: "Male",
  [Sex.FEMALE]: "Female",
};

export const REPRO_LABEL: Record<Reproduction, string> = {
  [Reproduction.NEUTERED]: "Neutered",
  [Reproduction.SPAYED]: "Spayed",
  [Reproduction.NOT_NEUTERED]: "Not neutered",
  [Reproduction.NOT_SPAYED]: "Not spayed",
};

export const BODY_LABEL: Record<BodyCondition, string> = {
  [BodyCondition.UNDERWEIGHT]: "Underweight",
  [BodyCondition.IDEAL]: "Ideal",
  [BodyCondition.OVERWEIGHT]: "Overweight",
};

export const safeStr = (v: any) => (typeof v === "string" ? v.trim() : "");

function toLabel<T extends Record<string, string>>(
  value: unknown,
  map: T
): string | undefined {
  const s = String(value ?? "").trim();
  if (!s) return undefined;

  return (map as any)[s] ?? s;
}

function toActivityLabel(value: unknown) {
  const s = String(value ?? "").trim();
  if (!s) return undefined;

  const meta = (ActivityLevelMeta as any)[s];
  if (meta?.label) return meta.label;

  return s;
}

function toTreatLabel(value: unknown) {
  const s = String(value ?? "").trim();
  if (!s) return undefined;

  const meta = (TreatFrequencyMeta as any)[s];
  if (meta?.label) return meta.label;

  return s;
}

type LabelledDog = {
  id: string;
  name?: string;
  breed?: string;

  sex?: string;
  reproduction?: string;
  bodyCondition?: string;
  activityLevel?: string;
  treatFrequency?: string;

  ageYears?: number;
  ageMonths?: number;
  weightKg?: number;

  allergies?: string[];
  healthIssues?: string[];
  healthIssuesOther?: string;

  currentFood?: string[];
  currentFoodBrand?: string;
  triedFresh?: string;
  fussiness?: string;
  feedingGoal?: string;

  dailyCalories?: number;
  dailyGrams?: number;

  selectedPackSize?: number;
  selectedPlanDays?: number;
};

export function buildDogForStorage(form: any): LabelledDog {
  const ageYears = form?.ageYears ?? form?.years;
  const ageMonths = form?.ageMonths ?? form?.months;

  return {
    id: String(form?.id ?? "").trim(),

    name: safeStr(form?.name) || undefined,
    breed: safeStr(form?.breed) || undefined,

    sex: toLabel(form?.sex, SEX_LABEL as any),
    reproduction: toLabel(form?.reproduction, REPRO_LABEL as any),
    bodyCondition: toLabel(form?.bodyCondition, BODY_LABEL as any),
    activityLevel: toActivityLabel(form?.activityLevel),
    treatFrequency: toTreatLabel(form?.treatFrequency),

    ageYears:
      ageYears !== "" && ageYears != null ? Number(ageYears) : undefined,
    ageMonths:
      ageMonths !== "" && ageMonths != null ? Number(ageMonths) : undefined,
    weightKg:
      form?.weightKg !== "" && form?.weightKg != null
        ? Number(form.weightKg)
        : undefined,

    // "No allergies" is a UI sentinel like the health-issues one: storing it
    // puts "NONE_OPTION" in front of customers (account page allergy badge,
    // Klaviyo emails) and in every allergy segment.
    allergies: Array.isArray(form?.allergies)
      ? form.allergies.map(String).filter((a: string) => a !== ALLERGY_NONE)
      : [],
    // "No health issues" is a UI sentinel, not a health issue — it must not
    // reach the customer record.
    healthIssues: Array.isArray(form?.healthIssues)
      ? form.healthIssues.map(String).filter((h: string) => h !== HEALTH_ISSUE_NONE)
      : [],
    healthIssuesOther: safeStr(form?.healthIssuesOther) || undefined,

    currentFood: Array.isArray(form?.currentFood)
      ? form.currentFood
          .map((v: string) => CURRENT_FOOD_LABEL[v as CurrentFood] ?? String(v))
      : [],
    currentFoodBrand: safeStr(form?.currentFoodBrand) || undefined,
    triedFresh: toLabel(form?.triedFresh, TRIED_FRESH_LABEL as any),
    fussiness: toLabel(form?.fussiness, FUSSINESS_LABEL as any),
    feedingGoal: toLabel(form?.feedingGoal, FEEDING_GOAL_LABEL as any),

    // Derived from grams so a stale persisted dailyCalories can't reach the
    // order (_paws_dog_json → customer metafield) or Klaviyo.
    dailyCalories:
      Number(form?.dailyGrams) > 0
        ? caloriesFromGrams(form.dailyGrams)
        : form?.dailyCalories != null
        ? Number(form.dailyCalories)
        : undefined,
    dailyGrams: form?.dailyGrams != null ? Number(form.dailyGrams) : undefined,

    selectedPackSize:
      form?.selectedPackSize != null
        ? Number(form.selectedPackSize)
        : undefined,
    selectedPlanDays:
      form?.selectedPlanDays != null
        ? Number(form.selectedPlanDays)
        : undefined,
  };
}

export enum Breeds {
  AFFENPINSCHER = "Affenpinscher",
  AFGHAN_HOUND = "Afghan Hound",
  AFRICANIS = "Africanis",
  AIDI = "Aidi",
  AIREDALE_TERRIER = "Airedale Terrier",
  AKBASH = "Akbash",
  AKITA = "Akita",
  AKSARAY_MALAKLISI = "Aksaray Malaklisi",
  ALANO_ESPANOL = "Alano Español",
  ALAPAHA_BLUE_BLOOD_BULLDOG = "Alapaha Blue Blood Bulldog",
  ALASKAN_HUSKY = "Alaskan husky",
  ALASKAN_KLEE_KAI = "Alaskan Klee Kai",
  ALASKAN_MALAMUTE = "Alaskan Malamute",
  ALOPEKIS = "Alopekis",
  ALPINE_DACHSBRACKE = "Alpine Dachsbracke",
  AMERICAN_BULLDOG = "American Bulldog",
  AMERICAN_BULLY = "American Bully",
  AMERICAN_COCKER_SPANIEL = "American Cocker Spaniel",
  AMERICAN_ENGLISH_COONHOUND = "American English Coonhound",
  AMERICAN_ESKIMO_DOG = "American Eskimo Dog",
  AMERICAN_FOXHOUND = "American Foxhound",
  AMERICAN_HAIRLESS_TERRIER = "American Hairless Terrier",
  AMERICAN_PIT_BULL_TERRIER = "American Pit Bull Terrier",
  AMERICAN_STAFFORDSHIRE_TERRIER = "American Staffordshire Terrier",
  AMERICAN_WATER_SPANIEL = "American Water Spaniel",
  ANATOLIAN_SHEPHERD_DOG = "Anatolian Shepherd Dog",
  ANDALUSIAN_HOUND = "Andalusian Hound",
  ANGLO_FRANCAIS_DE_PETITE_VENERIE = "Anglo-Français de Petite Vénerie",
  APPENZELLER_SENNENHUND = "Appenzeller Sennenhund",
  ARIEGEOIS = "Ariegeois",
  ARMANT = "Armant",
  ARMENIAN_GAMPR_DOG = "Armenian Gampr dog",
  ARTOIS_HOUND = "Artois Hound",
  ASSYRIAN_MASTIFF = "Assyrian Mastiff",
  AUSTRALIAN_CATTLE_DOG = "Australian Cattle Dog",
  AUSTRALIAN_KELPIE = "Australian Kelpie",
  AUSTRALIAN_SHEPHERD = "Australian Shepherd",
  AUSTRALIAN_STUMPY_TAIL_CATTLE_DOG = "Australian Stumpy Tail Cattle Dog",
  AUSTRALIAN_TERRIER = "Australian Terrier",
  AUSTRIAN_BLACK_AND_TAN_HOUND = "Austrian Black and Tan Hound",
  AUSTRIAN_PINSCHER = "Austrian Pinscher",
  AZAWAKH = "Azawakh",

  BAKHARWAL_DOG = "Bakharwal dog",
  BANJARA_HOUND = "Banjara Hound",
  BARBADO_DA_TERCEIRA = "Barbado da Terceira",
  BARBET = "Barbet",
  BASENJI = "Basenji",
  BASQUE_SHEPHERD_DOG = "Basque Shepherd Dog",
  BASSET_ARTESIEN_NORMAND = "Basset Artésien Normand",
  BASSET_BLEU_DE_GASCOGNE = "Basset Bleu de Gascogne",
  BASSET_FAUVE_DE_BRETAGNE = "Basset Fauve de Bretagne",
  BASSET_HOUND = "Basset Hound",
  BAVARIAN_MOUNTAIN_HOUND = "Bavarian Mountain Hound",
  BEAGLE = "Beagle",
  BEAGLE_HARRIER = "Beagle-Harrier",
  BEARDED_COLLIE = "Bearded Collie",
  BEAUCERON = "Beauceron",
  BEDLINGTON_TERRIER = "Bedlington Terrier",
  BELGIAN_MALINOIS = "Belgian Malinois",
  BELGIAN_SHEPHERD = "Belgian Shepherd",
  BELGIAN_TERVUREN = "Belgian Tervuren",
  BELGIAN_LAEKENOIS = "Belgian Laekenois",
  BERGAMASCO_SHEPHERD = "Bergamasco Shepherd",
  BERGER_PICARD = "Berger Picard",
  BERNESE_MOUNTAIN_DOG = "Bernese Mountain Dog",
  BICHON_FRISE = "Bichon Frisé",
  BIEWER_TERRIER = "Biewer Terrier",
  BILLY = "Billy",
  BLACK_AND_TAN_COONHOUND = "Black and Tan Coonhound",
  BLACK_MOUTH_CUR = "Black Mouth Cur",
  BLACK_NORWEGIAN_ELKHOUND = "Black Norwegian Elkhound",
  BLACK_RUSSIAN_TERRIER = "Black Russian Terrier",
  BLOODHOUND = "Bloodhound",
  BLUE_LACY = "Blue Lacy",
  BLUE_PICARDY_SPANIEL = "Blue Picardy Spaniel",
  BLUETICK_COONHOUND = "Bluetick Coonhound",
  BOERBOEL = "Boerboel",
  BOHEMIAN_SHEPHERD = "Bohemian Shepherd",
  BOLOGNESE = "Bolognese",
  BORDER_COLLIE = "Border Collie",
  BORDER_TERRIER = "Border Terrier",
  BORZOI = "Borzoi",
  BOSNIAN_COARSE_HAIRED_HOUND = "Bosnian Coarse-haired Hound",
  BOSTON_TERRIER = "Boston Terrier",
  BOUVIER_DES_ARDENNES = "Bouvier des Ardennes",
  BOUVIER_DES_FLANDRES = "Bouvier des Flandres",
  BOXER = "Boxer",
  BOYKIN_SPANIEL = "Boykin Spaniel",
  BRACCO_ITALIANO = "Bracco Italiano",
  BRAQUE_DAUVERGNE = "Braque d'Auvergne",
  BRAQUE_DE_LARIEGE = "Braque de l'Ariège",
  BRAQUE_DU_BOURBONNAIS = "Braque du Bourbonnais",
  BRAQUE_FRANCAIS = "Braque Francais",
  BRAQUE_SAINT_GERMAIN = "Braque Saint-Germain",
  BRIARD = "Briard",
  BRIQUET_GRIFFON_VENDEEN = "Briquet Griffon Vendéen",
  BRITTANY = "Brittany",
  BROHOLMER = "Broholmer",
  BRUNO_JURA_HOUND = "Bruno Jura Hound",
  BRUSSELS_GRIFFON = "Brussels Griffon",
  BUCOVINA_SHEPHERD_DOG = "Bucovina Shepherd Dog",
  BULL_ARAB = "Bull Arab",
  BULL_TERRIER = "Bull Terrier",
  BULLDOG = "Bulldog",
  BULLMASTIFF = "Bullmastiff",
  BULLY_KUTTA = "Bully Kutta",
  BURGOS_POINTER = "Burgos Pointer",
  BURYAT_MONGOLIAN_WOLFHOUND = "Buryat-Mongolian Wolfhound",

  CA_DE_BOU = "Ca de Bou",
  CA_ME_MALLORQUI = "Ca Mè Mallorquí",
  CAIRN_TERRIER = "Cairn Terrier",
  CALUPOH = "Calupoh",
  CAMPEIRO_BULLDOG = "Campeiro Bulldog",
  CAN_DE_CHIRA = "Can de Chira",
  CAN_DE_PALLEIRO = "Can de Palleiro",
  CANAAN_DOG = "Canaan Dog",
  CANADIAN_ESKIMO_DOG = "Canadian Eskimo Dog",
  CANE_CORSO = "Cane Corso",
  CANE_DI_OROPA = "Cane di Oropa",
  CANE_PARATORE = "Cane Paratore",
  CANTABRIAN_WATER_DOG = "Cantabrian Water Dog",
  CAO_DA_SERRA_DE_AIRES = "Cão da Serra de Aires",
  CAO_DE_CASTRO_LABOREIRO = "Cão de Castro Laboreiro",
  CAO_DE_GADO_TRANSMONTANO = "Cão de Gado Transmontano",
  CAO_FILA_DE_SAO_MIGUEL = "Cão Fila de São Miguel",
  CARDIGAN_WELSH_CORGI = "Cardigan Welsh Corgi",
  CAREA_CASTELLANO_MANCHEGO = "Carea Castellano Manchego",
  CAREA_LEONES = "Carea Leonés",
  CAROLINA_DOG = "Carolina Dog",
  CARPATHIAN_SHEPHERD_DOG = "Carpathian Shepherd Dog",
  CATAHOULA_LEOPARD_DOG = "Catahoula Leopard Dog",
  CATALAN_SHEEPDOG = "Catalan Sheepdog",
  CAUCASIAN_SHEPHERD_DOG = "Caucasian Shepherd Dog",
  CAVALIER_KING_CHARLES_SPANIEL = "Cavalier King Charles Spaniel",
  CENTRAL_ASIAN_SHEPHERD_DOG = "Central Asian Shepherd Dog",
  CESKY_FOUSEK = "Cesky Fousek",
  CESKY_TERRIER = "Cesky Terrier",
  CHESAPEAKE_BAY_RETRIEVER = "Chesapeake Bay Retriever",
  CHIEN_FRANCAIS_BLANC_ET_NOIR = "Chien Français Blanc et Noir",
  CHIEN_FRANCAIS_BLANC_ET_ORANGE = "Chien Français Blanc et Orange",
  CHIEN_FRANCAIS_TRICOLORE = "Chien Français Tricolore",
  CHIHUAHUA = "Chihuahua",
  CHILEAN_TERRIER = "Chilean Terrier",
  CHINESE_CRESTED_DOG = "Chinese Crested Dog",
  CHINESE_SHAR_PEI = "Chinese Shar-Pei",
  CHINOOK = "Chinook",
  CHIPPIPARAI = "Chippiparai",
  CHONGQING_DOG = "Chongqing dog",
  CHORTAI = "Chortai",
  CHOW_CHOW = "Chow Chow",
  CHUKOTKA_SLED_DOG = "Chukotka sled dog",
  CIMARRON_URUGUAYO = "Cimarrón Uruguayo",
  CIRNECO_DELLETNA = "Cirneco dell'Etna",
  CLUMBER_SPANIEL = "Clumber Spaniel",
  COCKER_SPANIEL = "Cocker Spaniel",
  COLOMBIAN_FINO_HOUND = "Colombian fino hound",
  COLLIE = "Collie",
  CONTINENTAL_BULLDOG = "Continental bulldog",
  COTON_DE_TULEAR = "Coton de Tulear",
  CRETAN_HOUND = "Cretan Hound",
  CROATIAN_SHEEPDOG = "Croatian Sheepdog",
  CURLY_COATED_RETRIEVER = "Curly-Coated Retriever",
  CURSINU = "Cursinu",
  CZECHOSLOVAKIAN_WOLFDOG = "Czechoslovakian Wolfdog",

  DACHSHUND = "Dachshund",
  DALMATIAN = "Dalmatian",
  DANDIE_DINMONT_TERRIER = "Dandie Dinmont Terrier",
  DANISH_SPITZ = "Danish Spitz",
  DANISH_SWEDISH_FARMDOG = "Danish-Swedish Farmdog",
  DENMARK_FEIST = "Denmark Feist",
  DOBERMANN = "Dobermann",
  DOGO_ARGENTINO = "Dogo Argentino",
  DOGO_GUATEMALTECO = "Dogo Guatemaltecο",
  DOGO_SARDESCO = "Dogo Sardesco",
  DOGUE_BRASILEIRO = "Dogue Brasileiro",
  DOGUE_DE_BORDEAUX = "Dogue de Bordeaux",
  DRENTSE_PATRIJSHOND = "Drentse Patrijshond",
  DREVER = "Drever",
  DUNKER = "Dunker",
  DUTCH_SHEPHERD = "Dutch Shepherd",
  DUTCH_SMOUSHOND = "Dutch Smoushond",

  EAST_SIBERIAN_LAIKA = "East Siberian Laika",
  EAST_EUROPEAN_SHEPHERD = "East European Shepherd",
  ECUADORIAN_HAIRLESS_DOG = "Ecuadorian Hairless Dog",
  ENGLISH_BULLDOG = "English Bulldog",
  ENGLISH_COCKER_SPANIEL = "English Cocker Spaniel",
  ENGLISH_FOXHOUND = "English Foxhound",
  ENGLISH_MASTIFF = "English Mastiff",
  ENGLISH_SETTER = "English Setter",
  ENGLISH_SHEPHERD = "English Shepherd",
  ENGLISH_SPRINGER_SPANIEL = "English Springer Spaniel",
  ENGLISH_TOY_TERRIER_BLACK_AND_TAN = "English Toy Terrier (Black & Tan)",
  ENTLEBUCHER_MOUNTAIN_DOG = "Entlebucher Mountain Dog",
  ESTONIAN_HOUND = "Estonian Hound",
  ESTRELA_MOUNTAIN_DOG = "Estrela Mountain Dog",
  EURASIER = "Eurasier",

  FIELD_SPANIEL = "Field Spaniel",
  FILA_BRASILEIRO = "Fila Brasileiro",
  FINNISH_HOUND = "Finnish Hound",
  FINNISH_LAPPHUND = "Finnish Lapphund",
  FINNISH_SPITZ = "Finnish Spitz",
  FLAT_COATED_RETRIEVER = "Flat-Coated Retriever",
  FOX_TERRIER = "Fox Terrier",
  FRENCH_BULLDOG = "French Bulldog",
  FRENCH_SPANIEL = "French Spaniel",

  GALGO_ESPANOL = "Galgo Español",
  GARAFIAN_SHEPHERD = "Garafian Shepherd",
  GASCON_SAINTONGEOIS = "Gascon Saintongeois",
  GEORGIAN_SHEPHERD = "Georgian Shepherd",
  GERMAN_HOUND = "German Hound",
  GERMAN_LONGHAIRED_POINTER = "German Longhaired Pointer",
  GERMAN_PINSCHER = "German Pinscher",
  GERMAN_ROUGHHAIRED_POINTER = "German Roughhaired Pointer",
  GERMAN_SHEPHERD = "German Shepherd",
  GERMAN_SHORTHAIRED_POINTER = "German Shorthaired Pointer",
  GERMAN_SPANIEL = "German Spaniel",
  GERMAN_SPITZ = "German Spitz",
  GERMAN_WIREHAIRED_POINTER = "German Wirehaired Pointer",
  GIANT_SCHNAUZER = "Giant Schnauzer",
  GLEN_OF_IMAAL_TERRIER = "Glen of Imaal Terrier",
  GOLDEN_RETRIEVER = "Golden Retriever",
  GOLDA_DOR = "Goldador",
  GONCZY_POLSKI = "Gończy Polski",
  GORDON_SETTER = "Gordon Setter",
  GRAND_ANGLO_FRANCAIS_BLANC_ET_NOIR = "Grand Anglo-Français Blanc et Noir",
  GRAND_ANGLO_FRANCAIS_BLANC_ET_ORANGE = "Grand Anglo-Français Blanc et Orange",
  GRAND_ANGLO_FRANCAIS_TRICOLORE = "Grand Anglo-Français Tricolore",
  GRAND_BASSET_GRIFFON_VENDEEN = "Grand Basset Griffon Vendéen",
  GRAND_BLEU_DE_GASCOGNE = "Grand Bleu de Gascogne",
  GRAND_GRIFFON_VENDEEN = "Grand Griffon Vendéen",
  GREAT_DANE = "Great Dane",
  GREAT_PYRENEES = "Great Pyrenees",
  GREATER_SWISS_MOUNTAIN_DOG = "Greater Swiss Mountain Dog",
  GREEK_HAREHOUND = "Greek Harehound",
  GREEK_SHEPHERD = "Greek Shepherd",
  GREENLAND_DOG = "Greenland Dog",
  GREYHOUND = "Greyhound",
  GRIFFON_BLEU_DE_GASCOGNE = "Griffon Bleu de Gascogne",
  GRIFFON_FAUVE_DE_BRETAGNE = "Griffon Fauve de Bretagne",
  GRIFFON_NIVERNAIS = "Griffon Nivernais",
  GULL_DONG = "Gull Dong",
  GULL_TERRIER = "Gull Terrier",

  HALLEFORS_ELKHOUND = "Hällefors Elkhound",
  HALDEN_HOUND = "Halden Hound",
  HAMILTONSTOVARE = "Hamiltonstövare",
  HANOVER_HOUND = "Hanover Hound",
  HARRIER = "Harrier",
  HAVANESE = "Havanese",
  HIMALAYAN_SHEEPDOG = "Himalayan Sheepdog",
  HIERRAN_WOLFDOG = "Hierran Wolfdog",
  HOKKAIDO = "Hokkaido",
  HOVAWART = "Hovawart",
  HUNTAWAY = "Huntaway",
  HYGEN_HOUND = "Hygen Hound",

  IBIZAN_HOUND = "Ibizan Hound",
  ICELANDIC_SHEEPDOG = "Icelandic Sheepdog",
  INDIAN_PARIAH_DOG = "Indian pariah dog",
  INDIAN_SPITZ = "Indian Spitz",
  IRISH_RED_AND_WHITE_SETTER = "Irish Red and White Setter",
  IRISH_SETTER = "Irish Setter",
  IRISH_TERRIER = "Irish Terrier",
  IRISH_WATER_SPANIEL = "Irish Water Spaniel",
  IRISH_WOLFHOUND = "Irish Wolfhound",
  ISTRIAN_COARSE_HAIRED_HOUND = "Istrian Coarse-haired Hound",
  ISTRIAN_SHORTHAIRED_HOUND = "Istrian Shorthaired Hound",
  ITALIAN_GREYHOUND = "Italian Greyhound",

  JACK_RUSSELL_TERRIER = "Jack Russell Terrier",
  JAGDTERRIER = "Jagdterrier",
  JAPANESE_CHIN = "Japanese Chin",
  JAPANESE_SPITZ = "Japanese Spitz",
  JAPANESE_TERRIER = "Japanese Terrier",
  JINDO = "Jindo",
  JONANGI = "Jonangi",

  KAI_KEN = "Kai Ken",
  KAIKADI = "Kaikadi",
  KANGAL_SHEPHERD_DOG = "Kangal Shepherd Dog",
  KANNI = "Kanni",
  KARAKACHAN_DOG = "Karakachan dog",
  KARELIAN_BEAR_DOG = "Karelian Bear Dog",
  KARS = "Kars",
  KARST_SHEPHERD = "Karst Shepherd",
  KEESHOND = "Keeshond",
  KERRY_BEAGLE = "Kerry Beagle",
  KERRY_BLUE_TERRIER = "Kerry Blue Terrier",
  KING_CHARLES_SPANIEL = "King Charles Spaniel",
  KING_SHEPHERD = "King Shepherd",
  KINTAMANI = "Kintamani",
  KISHU = "Kishu",
  KOKONI = "Kokoni",
  KOMBAI = "Kombai",
  KOMONDOR = "Komondor",
  KOOIKERHONDJE = "Kooikerhondje",
  KOOLIE = "Koolie",
  KOYUN_DOG = "Koyun dog",
  KROMFOHRLANDER = "Kromfohrländer",
  KUCHI = "Kuchi",
  KUVASZ = "Kuvasz",

  LABRADOR_RETRIEVER = "Labrador Retriever",
  LABRADOODLE = "Labradoodle",
  LAGOTTO_ROMAGNOLO = "Lagotto Romagnolo",
  LAKELAND_TERRIER = "Lakeland Terrier",
  LANCASHIRE_HEELER = "Lancashire Heeler",
  LANDSEER = "Landseer",
  LAPPONIAN_HERDER = "Lapponian Herder",
  LARGE_MUNSTERLANDER = "Large Münsterländer",
  LEONBERGER = "Leonberger",
  LEVRIERO_SARDO = "Levriero Sardo",
  LHASA_APSO = "Lhasa Apso",
  LIANGSHAN_DOG = "Liangshan Dog",
  LITHUANIAN_HOUND = "Lithuanian Hound",
  LOBITO_HERRENO = "Lobito Herreño",
  LOWCHEN = "Löwchen",
  LUPO_ITALIANO = "Lupo Italiano",

  MACKENZIE_RIVER_HUSKY = "Mackenzie River husky",
  MAGYAR_AGAR = "Magyar agár",
  MAHRATTA_GREYHOUND = "Mahratta Greyhound",
  MALTESE = "Maltese",
  MANCHESTER_TERRIER = "Manchester Terrier",
  MANETO = "Maneto",
  MAREMMANO_ABRUZZESE_SHEEPDOG = "Maremmano-Abruzzese Sheepdog",
  MCNAB_DOG = "McNab dog",
  MINIATURE_AMERICAN_SHEPHERD = "Miniature American Shepherd",
  MINIATURE_BULL_TERRIER = "Miniature Bull Terrier",
  MINIATURE_FOX_TERRIER = "Miniature Fox Terrier",
  MINIATURE_PINSCHER = "Miniature Pinscher",
  MINIATURE_POODLE = "Miniature Poodle",
  MINIATURE_SCHNAUZER = "Miniature Schnauzer",
  MOLOSSUS_OF_EPIRUS = "Molossus of Epirus",
  MONTENEGRIN_MOUNTAIN_HOUND = "Montenegrin Mountain Hound",
  MOUNTAIN_CUR = "Mountain Cur",
  MOUNTAIN_FEIST = "Mountain Feist",
  MUCUCHIES = "Mucuchies",
  MUDHOL_HOUND = "Mudhol Hound",
  MUDI = "Mudi",

  NEAPOLITAN_MASTIFF = "Neapolitan Mastiff",
  NENETS_HERDING_LAIKA = "Nenets Herding Laika",
  NEW_GUINEA_SINGING_DOG = "New Guinea singing dog",
  NEW_ZEALAND_HEADING_DOG = "New Zealand Heading Dog",
  NEWFOUNDLAND = "Newfoundland",
  NORFOLK_TERRIER = "Norfolk Terrier",
  NORRBOTTENSPETS = "Norrbottenspets",
  NORTHERN_INUIT_DOG = "Northern Inuit Dog",
  NORWEGIAN_BUHUND = "Norwegian Buhund",
  NORWEGIAN_ELKHOUND = "Norwegian Elkhound",
  NORWEGIAN_LUNDEHUND = "Norwegian Lundehund",
  NORWICH_TERRIER = "Norwich Terrier",
  NOVA_SCOTIA_DUCK_TOLLING_RETRIEVER = "Nova Scotia Duck Tolling Retriever",

  OLD_DANISH_POINTER = "Old Danish Pointer",
  OLD_ENGLISH_SHEEPDOG = "Old English Sheepdog",
  OLD_ENGLISH_TERRIER = "Old English Terrier",
  OLDE_ENGLISH_BULLDOGGE = "Olde English Bulldogge",
  OTTERHOUND = "Otterhound",

  PACHON_NAVARRO = "Pachon Navarro",
  PAMPAS_DEERHOUND = "Pampas Deerhound",
  PAPILLON = "Papillon",
  PARSON_RUSSELL_TERRIER = "Parson Russell Terrier",
  PASTORE_DELLA_LESSINIA_E_DEL_LAGORAI = "Pastore della Lessinia e del Lagorai",
  PATAGONIAN_SHEEPDOG = "Patagonian Sheepdog",
  PATTERDALE_TERRIER = "Patterdale Terrier",
  PEKINGESE = "Pekingese",
  PEMBROKE_WELSH_CORGI = "Pembroke Welsh Corgi",
  PERRO_MAJORERO = "Perro Majorero",
  PERRO_DE_PASTOR_MALLORQUIN = "Perro de Pastor Mallorquin",
  PERRO_DE_PRESA_CANARIO = "Perro de Presa Canario",
  PERRO_DE_PRESA_MALLORQUIN = "Perro de Presa Mallorquin",
  PERUVIAN_INCA_ORCHID = "Peruvian Inca Orchid",
  PETIT_BASSET_GRIFFON_VENDEEN = "Petit Basset Griffon Vendéen",
  PETIT_BLEU_DE_GASCOGNE = "Petit Bleu de Gascogne",
  PHALENE = "Phalène",
  PHARAOH_HOUND = "Pharaoh Hound",
  PHU_QUOC_RIDGEBACK = "Phu Quoc Ridgeback",
  PICARDY_SPANIEL = "Picardy Spaniel",
  PLUMMER_TERRIER = "Plummer Terrier",
  PLOTT_HOUND = "Plott Hound",
  PODENCO_CANARIO = "Podenco Canario",
  PODENCO_VALENCIANO = "Podenco Valenciano",
  POINTER = "Pointer",
  POITEVIN = "Poitevin",
  POLISH_GREYHOUND = "Polish Greyhound",
  POLISH_HOUND = "Polish Hound",
  POLISH_LOWLAND_SHEEPDOG = "Polish Lowland Sheepdog",
  POLISH_TATRA_SHEEPDOG = "Polish Tatra Sheepdog",
  POMERANIAN = "Pomeranian",
  PONT_AUDEMER_SPANIEL = "Pont-Audemer Spaniel",
  POODLE = "Poodle",
  PORCELAINE = "Porcelaine",
  PORTUGUESE_PODENGO = "Portuguese Podengo",
  PORTUGUESE_POINTER = "Portuguese Pointer",
  PORTUGUESE_WATER_DOG = "Portuguese Water Dog",
  POSAVAC_HOUND = "Posavac Hound",
  PRAZSKY_KRYSARIK = "Pražský Krysařík",
  PSHDAR_DOG = "Pshdar dog",
  PUDELPOINTER = "Pudelpointer",
  PUG = "Pug",
  PULI = "Puli",
  PUMI = "Pumi",
  PUNGSAN_DOG = "Pungsan dog",

  PYRENEAN_MASTIFF = "Pyrenean Mastiff",
  PYRENEAN_MOUNTAIN_DOG = "Pyrenean Mountain Dog",
  PYRENEAN_SHEEPDOG = "Pyrenean Sheepdog",

  RAFEIRO_DO_ALENTEJO = "Rafeiro do Alentejo",
  RAJAPALAYAM = "Rajapalayam",
  RAMPUR_GREYHOUND = "Rampur Greyhound",
  RAT_TERRIER = "Rat Terrier",
  RATONERO_BODEGUERO_ANDALUZ = "Ratonero Bodeguero Andaluz",
  RATONERO_MALLORQUIN = "Ratonero Mallorquin",
  RATONERO_MURCIANO = "Ratonero Murciano",
  RATONERO_VALENCIANO = "Ratonero Valenciano",
  REDBONE_COONHOUND = "Redbone Coonhound",
  RHODESIAN_RIDGEBACK = "Rhodesian Ridgeback",
  ROMANIAN_MIORITIC_SHEPHERD_DOG = "Romanian Mioritic Shepherd Dog",
  ROMANIAN_RAVEN_SHEPHERD_DOG = "Romanian Raven Shepherd Dog",
  ROTTWEILER = "Rottweiler",
  ROUGH_COLLIE = "Rough Collie",
  RUSSIAN_SPANIEL = "Russian Spaniel",
  RUSSIAN_TOY = "Russian Toy",
  RUSSO_EUROPEAN_LAIKA = "Russo-European Laika",

  SAARLOOS_WOLFDOG = "Saarloos Wolfdog",
  SABUESO_ESPANOL = "Sabueso Español",
  SAINT_BERNARD = "Saint Bernard",
  SAINT_HUBERT_JURA_HOUND = "Saint Hubert Jura Hound",
  SAINT_USUGE_SPANIEL = "Saint-Usuge Spaniel",
  SALUKI = "Saluki",
  SAMOYED = "Samoyed",
  SAPSALI = "Sapsali",
  SARABI_DOG = "Sarabi dog",
  SARDINIAN_SHEPHERD_DOG = "Sardinian Shepherd Dog",
  SARPLANINAC = "Šarplaninac",
  SCHAPENDOES = "Schapendoes",
  SCHILLERSTOVARE = "Schillerstövare",
  SCHIPPERKE = "Schipperke",
  SCHWEIZER_LAUFHUND = "Schweizer Laufhund",
  SCHWEIZERISCHER_NIEDERLAUFHUND = "Schweizerischer Niederlaufhund",
  SCOTTISH_DEERHOUND = "Scottish Deerhound",
  SCOTTISH_TERRIER = "Scottish Terrier",
  SEALYHAM_TERRIER = "Sealyham Terrier",
  SEGUGIO_DELLAPPENNINO = "Segugio dell'Appennino",
  SEGUGIO_ITALIANO = "Segugio Italiano",
  SEGUGIO_MAREMMANO = "Segugio Maremmano",
  SERBIAN_HOUND = "Serbian Hound",
  SERBIAN_TRICOLOUR_HOUND = "Serbian Tricolour Hound",
  SERRANO_BULLDOG = "Serrano Bulldog",
  SHAR_PEI = "Shar Pei",
  SHETLAND_SHEEPDOG = "Shetland Sheepdog",
  SHIBA_INU = "Shiba Inu",
  SHIH_TZU = "Shih Tzu",
  SHIKOKU = "Shikoku",
  SHILOH_SHEPHERD = "Shiloh Shepherd",
  SIBERIAN_HUSKY = "Siberian Husky",
  SILKEN_WINDHOUND = "Silken Windhound",
  SILKY_TERRIER = "Silky Terrier",
  SINHALA_HOUND = "Sinhala Hound",
  SKYE_TERRIER = "Skye Terrier",
  SLOUGHI = "Sloughi",
  SLOVAKIAN_WIREHAIRED_POINTER = "Slovakian Wirehaired Pointer",
  SLOVENSKY_CUVAC = "Slovenský Cuvac",
  SLOVENSKY_KOPOV = "Slovenský Kopov",
  SMALANDSTOVARE = "Smalandstövare",
  SMALL_GREEK_DOMESTIC_DOG = "Small Greek domestic dog",
  SMALL_MUNSTERLANDER = "Small Münsterländer",
  SMOOTH_COLLIE = "Smooth Collie",
  SMOOTH_FOX_TERRIER = "Smooth Fox Terrier",
  SOFT_COATED_WHEATEN_TERRIER = "Soft-Coated Wheaten Terrier",
  SOUTH_RUSSIAN_OVCHARKA = "South Russian Ovcharka",
  SPANISH_MASTIFF = "Spanish Mastiff",
  SPANISH_WATER_DOG = "Spanish Water Dog",
  SPINONE_ITALIANO = "Spinone Italiano",
  SPORTING_LUCAS_TERRIER = "Sporting Lucas Terrier",
  STABYHOUN = "Stabyhoun",
  STAFFORDSHIRE_BULL_TERRIER = "Staffordshire Bull Terrier",
  STANDARD_SCHNAUZER = "Standard Schnauzer",
  STEPHENS_STOCK = "Stephens Stock",
  STYRIAN_COARSE_HAIRED_HOUND = "Styrian Coarse-haired Hound",
  SUSSEX_SPANIEL = "Sussex Spaniel",
  SWEDISH_ELKHOUND = "Swedish Elkhound",
  SWEDISH_LAPPHUND = "Swedish Lapphund",
  SWEDISH_VALLHUND = "Swedish Vallhund",
  SWEDISH_WHITE_ELKHOUND = "Swedish White Elkhound",

  TAIGAN = "Taigan",
  TAIWAN_DOG = "Taiwan Dog",
  TAMASKAN_DOG = "Tamaskan Dog",
  TANG_DOG = "Tang Dog",
  TAZY = "Tazy",
  TEDDY_ROOSEVELT_TERRIER = "Teddy Roosevelt Terrier",
  TELOMIAN = "Telomian",
  TENTERFIELD_TERRIER = "Tenterfield Terrier",
  TERRIER_BRASILEIRO = "Terrier Brasileiro",
  THAI_BANGKAEW_DOG = "Thai Bangkaew Dog",
  THAI_RIDGEBACK = "Thai Ridgeback",
  TIBETAN_KYI_APSO = "Tibetan Kyi Apso",
  TIBETAN_MASTIFF = "Tibetan Mastiff",
  TIBETAN_SPANIEL = "Tibetan Spaniel",
  TIBETAN_TERRIER = "Tibetan Terrier",
  TORNJAK = "Tornjak",
  TOSA_INU = "Tosa Inu",
  TOY_FOX_TERRIER = "Toy Fox Terrier",
  TOY_MANCHESTER_TERRIER = "Toy Manchester Terrier",
  TRANSYLVANIAN_HOUND = "Transylvanian Hound",
  TREEING_CUR = "Treeing Cur",
  TREEING_FEIST = "Treeing Feist",
  TREEING_TENNESSEE_BRINDLE = "Treeing Tennessee Brindle",
  TREEING_WALKER_COONHOUND = "Treeing Walker Coonhound",
  TRIGG_HOUND = "Trigg Hound",
  TYROLEAN_HOUND = "Tyrolean Hound",

  VIZSLA = "Vizsla",
  VOLPINO_ITALIANO = "Volpino Italiano",

  WEIMARANER = "Weimaraner",
  WELSH_SHEEPDOG = "Welsh Sheepdog",
  WELSH_SPRINGER_SPANIEL = "Welsh Springer Spaniel",
  WELSH_TERRIER = "Welsh Terrier",
  WEST_COUNTRY_HARRIER = "West Country Harrier",
  WEST_HIGHLAND_WHITE_TERRIER = "West Highland White Terrier",
  WEST_SIBERIAN_LAIKA = "West Siberian Laika",
  WESTPHALIAN_DACHSBRACKE = "Westphalian Dachsbracke",
  WETTERHOUN = "Wetterhoun",
  WHIPPET = "Whippet",
  WHITE_SHEPHERD = "White Shepherd",
  WHITE_SWISS_SHEPHERD_DOG = "White Swiss Shepherd Dog",
  WIRE_FOX_TERRIER = "Wire Fox Terrier",
  WIREHAIRED_POINTING_GRIFFON = "Wirehaired Pointing Griffon",
  WIREHAIRED_VIZSLA = "Wirehaired Vizsla",

  XOLOITZCUINTLE = "Xoloitzcuintle",

  YAKUTIAN_LAIKA = "Yakutian Laika",
  YORKSHIRE_TERRIER = "Yorkshire Terrier",

  ZERDAVA = "Zerdava",
  COCKAPOO = "Cockapoo",
  CAVAPOO = "Cavapoo",
  CAVA_CHON = "Cava-Chon",
  GOLDENDOODLE = "Goldendoodle",
  BERNEDOODLE = "Bernedoodle",
  SHEEPADOODLE = "Sheepadoodle",
  SCHNOODLE = "Schnoodle",
  MALTIPOO = "Maltipoo",
  POMSKY = "Pomsky",
  PUGGLE = "Puggle",
  YORKIPOO = "Yorkipoo",
  CHIWEENIE = "Chiweenie",
  LABRADOODLE_STANDARD = "Labradoodle (Standard)",
  LABRADOODLE_MINIATURE = "Labradoodle (Miniature)",
  GOLDEN_DOODLE_STANDARD = "Goldendoodle (Standard)",
  GOLDEN_DOODLE_MINIATURE = "Goldendoodle (Miniature)",
  BULLDOG_MIX = "Bulldog Mix",
  SHEPHERD_MIX = "Shepherd Mix",
  HUSKY_MIX = "Husky Mix",
  TERRIER_MIX = "Terrier Mix",
  RETRIEVER_MIX = "Retriever Mix",

  MIXED_BREED = "Mixed breed",
  OTHER = "Other",
}
